"use client"

import { useCallback, useMemo } from 'react';
import { CommissionTransaction , CommissionPayout, PayoutConfirmationDto, CommissionPayoutSummaryDto, CommissionUser, OpeningBalanceRequest, CommissionPeriod } from '@/types/commission-dashboard';
import { API_ENDPOINTS , useApi } from '@/lib/api';
import { parseISO } from 'date-fns';
import { logger } from '@/lib/logger';

/**
 * Parses a date string to local midnight, timezone-safely.
 *
 * `new Date('2026-05-01')` parses a date-only string as UTC midnight, which in a
 * negative-offset timezone (e.g. America/Toronto) rolls back to the previous local day.
 * `parseISO` from date-fns treats a date-only string as local time, so day-level
 * comparisons stay correct. The result is normalized to local midnight so only the
 * calendar day matters.
 */
export function parseDateOnlyLocal(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const parsed = parseISO(value);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

/**
 * Returns true if a transaction is "available for payout" based on the current payout type:
 * - Scheduled: transactions with a scheduledPaymentDate up to and including the current period's plannedPaymentDate
 * - Adhoc: transactions with a scheduledPaymentDate up to the current date
 * Transactions with no scheduledPaymentDate are never available for payout (they go to Future Scheduled).
 */
export function isAvailableForPayout(
  transaction: CommissionTransaction,
  payoutType: 'scheduled' | 'adhoc',
  plannedPaymentDate: string
): boolean {
  if (!transaction.scheduledPaymentDate) return false;
  const scheduledDate = parseDateOnlyLocal(transaction.scheduledPaymentDate);
  if (!scheduledDate) return false;

  if (payoutType === 'adhoc') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return scheduledDate <= today;
  }

  const periodDate = parseDateOnlyLocal(plannedPaymentDate);
  if (!periodDate) return false;
  return scheduledDate <= periodDate;
}

export function useCommissionDashboard() {
  const { commissionServiceApi } = useApi();

  const getUnpaidTransactions = useCallback(async (): Promise<CommissionTransaction[]> => {
    try {
      return await commissionServiceApi.get<CommissionTransaction[]>(API_ENDPOINTS.COMMISSION.TRANSACTION.UNPAID);
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const scheduleTransactions = useCallback(async (transactionIds: number[], scheduledDate: Date): Promise<void> => {
    try {
      await commissionServiceApi.put(API_ENDPOINTS.COMMISSION.TRANSACTION.SCHEDULE, {
        transactionIds,
        scheduledDate: scheduledDate.toISOString(),
      });
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const deferTransactions = useCallback(async (transactionIds: number[], deferToDate: Date): Promise<void> => {
    try {
      await commissionServiceApi.put(API_ENDPOINTS.COMMISSION.TRANSACTION.DEFER, {
        transactionIds,
        scheduledDate: deferToDate.toISOString(),
      });
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const getLatestPayout = useCallback(async (): Promise<CommissionPayout> => {
    try {
      const response = await commissionServiceApi.get<CommissionPayout>(API_ENDPOINTS.COMMISSION.PAYOUT.LATEST_PAYOUT);
      return response;
    } catch (error) {
      logger.error(error);
      return {
        id: 0,
        payoutDate: '',
        statementDate: '',
        isAdhoc: false,
        createdDatetime: '',
        totalUsers: 0,
        totalCommissionAmount: 0,
        standardCommissionAmount: 0,
        permanentPlacementCommissionAmount: 0,
        draws: 0,
        adjustments: 0,
        openingBalance: 0,
        netChange: 0,
        netPayable: 0,
        closingBalance: 0,
        notes: ''
      };
    }
  }, [commissionServiceApi]);

  const getCommissionUsers = useCallback(async (): Promise<CommissionUser[]> => {
    try {
      return await commissionServiceApi.get<CommissionUser[]>(API_ENDPOINTS.COMMISSION.TRANSACTION.USERS);
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const getOpeningBalances = useCallback(async (request: OpeningBalanceRequest): Promise<Record<number, number>> => {
    try {
      return await commissionServiceApi.post<Record<number, number>>(
        API_ENDPOINTS.COMMISSION.STATEMENT.OPENING_BALANCES,
        request
      );
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const confirmPayout = useCallback(async (data: PayoutConfirmationDto): Promise<void> => {
    try {
      await commissionServiceApi.post(API_ENDPOINTS.COMMISSION.PAYOUT.PAYOUT, data);
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const getPayoutHistory = useCallback(async (year: number): Promise<CommissionPayout[]> => {
    try {
      const response = await commissionServiceApi.get<CommissionPayout[]>(`${API_ENDPOINTS.COMMISSION.PAYOUT.PAYOUT_HISTORY}/${year}`);
      return response || [];
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const getPayoutYears = useCallback(async (): Promise<number[]> => {
    try {
      const response = await commissionServiceApi.get<number[]>(API_ENDPOINTS.COMMISSION.PAYOUT.PAYOUT_YEARS);
      return response || [];
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const getPayoutDetails = useCallback(async (payoutId: number): Promise<CommissionPayoutSummaryDto> => {
    try {
      const response = await commissionServiceApi.get<CommissionPayoutSummaryDto>(`${API_ENDPOINTS.COMMISSION.PAYOUT.PAYOUT_DETAILS}/${payoutId}`);
      return response || { payout: {} as CommissionPayout, transactions: [] };
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const generateRecurringAdjustments = useCallback(async (): Promise<{ transactionsCreated: number }> => {
    try {
      const response = await commissionServiceApi.post<{ transactionsCreated: number }>(
        API_ENDPOINTS.COMMISSION.TRANSACTION.GENERATE_RECURRING,
        {}
      );
      return response;
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  const getCurrentPeriod = useCallback(async (): Promise<CommissionPeriod | null> => {
    try {
      return await commissionServiceApi.get<CommissionPeriod>(API_ENDPOINTS.COMMISSION.PAYOUT.CURRENT_PERIOD);
    } catch (error) {
      logger.error(error);
      return null;
    }
  }, [commissionServiceApi]);

  const finalizePeriod = useCallback(async (timesheetEndDate: string): Promise<void> => {
    try {
      await commissionServiceApi.post(
        API_ENDPOINTS.COMMISSION.PAYOUT.FINALIZE_PERIOD,
        { timesheetEndDate }
      );
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }, [commissionServiceApi]);

  return useMemo(() => ({
    getUnpaidTransactions,
    scheduleTransactions,
    deferTransactions,
    getLatestPayout,
    confirmPayout,
    getPayoutHistory,
    getPayoutYears,
    getPayoutDetails,
    generateRecurringAdjustments,
    getCommissionUsers,
    getOpeningBalances,
    getCurrentPeriod,
    finalizePeriod
  }), [
    getUnpaidTransactions,
    scheduleTransactions,
    deferTransactions,
    getLatestPayout,
    confirmPayout,
    getPayoutHistory,
    getPayoutYears,
    getPayoutDetails,
    generateRecurringAdjustments,
    getCommissionUsers,
    getOpeningBalances,
    getCurrentPeriod,
    finalizePeriod
  ]);
}
